import { useId } from 'react'

const Beaker = ({
  size = 500,
  fillLevel = 0,
  fillColor = '#7DD3FC',
  muddy = false,
  cleared = false,
  precipitate = 0,
  label = '',
  onClick,
  active = false,
  disabled = false,
  className = ''
}) => {
  const id = useId()
  const width = size === 500 ? 130 : 100
  const height = size === 500 ? 170 : 130

  // Calculate liquid color based on state
  const liquidColor = cleared ? '#B8E6F2' : muddy ? '#8B7355' : fillColor

  // Beaker dimensions (cylindrical shape)
  const beakerLeft = 25
  const beakerRight = 115
  const beakerTop = 25
  const beakerBottom = 145
  const beakerWidth = beakerRight - beakerLeft

  // Calculate fill height
  const innerHeight = beakerBottom - beakerTop - 10
  const fillHeight = (fillLevel / 100) * innerHeight
  const fillTop = beakerBottom - 5 - fillHeight
  const precipHeight = precipitate * 20

  return (
    <svg
      viewBox="0 0 140 170"
      width={width}
      height={height}
      onClick={disabled ? undefined : onClick}
      style={{
        cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease'
      }}
      className={className}
    >
      <defs>
        {/* Glass gradient - horizontal for cylindrical look */}
        <linearGradient id={`glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(170,200,220,0.8)" />
          <stop offset="8%" stopColor="rgba(220,240,255,0.5)" />
          <stop offset="15%" stopColor="rgba(255,255,255,0.3)" />
          <stop offset="30%" stopColor="rgba(200,230,245,0.15)" />
          <stop offset="70%" stopColor="rgba(200,230,245,0.15)" />
          <stop offset="85%" stopColor="rgba(255,255,255,0.25)" />
          <stop offset="92%" stopColor="rgba(220,240,255,0.4)" />
          <stop offset="100%" stopColor="rgba(170,200,220,0.7)" />
        </linearGradient>

        {/* Liquid gradient */}
        <linearGradient id={`liquid-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={liquidColor} stopOpacity="0.85" />
          <stop offset="100%" stopColor={liquidColor} stopOpacity="0.95" />
        </linearGradient>

        {/* Muddy water with particles */}
        {muddy && (
          <pattern id={`muddy-${id}`} patternUnits="userSpaceOnUse" width="10" height="10">
            <rect width="10" height="10" fill="#8B7355"/>
            <circle cx="3" cy="3" r="1.2" fill="#6B5344" opacity="0.6"/>
            <circle cx="8" cy="6" r="0.8" fill="#5B4334" opacity="0.5"/>
            <circle cx="5" cy="9" r="1" fill="#7B6354" opacity="0.4"/>
          </pattern>
        )}

        {/* Precipitate gradient */}
        <linearGradient id={`precip-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#D4C8A8" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#8A7858" stopOpacity="0.95" />
        </linearGradient>

        {/* Rim gradient */}
        <linearGradient id={`rim-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#A0C0D8" />
          <stop offset="50%" stopColor="#7A9BB8" />
          <stop offset="100%" stopColor="#5A7A95" />
        </linearGradient>

        {/* Bottom ellipse gradient */}
        <radialGradient id={`bottom-${id}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(200,220,240,0.4)" />
          <stop offset="100%" stopColor="rgba(150,180,200,0.6)" />
        </radialGradient>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-20%" y="-10%" width="140%" height="130%">
          <feDropShadow dx="3" dy="4" stdDeviation="3" floodOpacity="0.3"/>
        </filter>

        {/* Clip for liquid */}
        <clipPath id={`liquid-clip-${id}`}>
          <rect x={beakerLeft + 3} y={beakerTop + 5} width={beakerWidth - 6} height={beakerBottom - beakerTop - 8} />
        </clipPath>
      </defs>

      {/* Active glow */}
      {active && (
        <rect
          x={beakerLeft - 8} y={beakerTop - 8}
          width={beakerWidth + 16} height={beakerBottom - beakerTop + 16}
          rx="8"
          fill="none"
          stroke="var(--primary)"
          strokeWidth="3"
          style={{ animation: 'glow 1.5s ease-in-out infinite' }}
        />
      )}

      {/* === CYLINDRICAL BEAKER === */}
      <g filter={`url(#shadow-${id})`}>

        {/* Back wall (for depth) */}
        <rect
          x={beakerLeft}
          y={beakerTop}
          width={beakerWidth}
          height={beakerBottom - beakerTop}
          fill="rgba(180,200,220,0.1)"
          rx="2"
        />

        {/* Left wall - straight vertical */}
        <line
          x1={beakerLeft} y1={beakerTop}
          x2={beakerLeft} y2={beakerBottom}
          stroke="#7A9BB8"
          strokeWidth="3"
          strokeLinecap="round"
        />

        {/* Right wall - straight vertical */}
        <line
          x1={beakerRight} y1={beakerTop}
          x2={beakerRight} y2={beakerBottom}
          stroke="#7A9BB8"
          strokeWidth="3"
          strokeLinecap="round"
        />

        {/* Bottom - flat with slight curve for 3D effect */}
        <path
          d={`M${beakerLeft} ${beakerBottom}
              Q${beakerLeft + beakerWidth/2} ${beakerBottom + 8} ${beakerRight} ${beakerBottom}`}
          fill="none"
          stroke="#7A9BB8"
          strokeWidth="3"
          strokeLinecap="round"
        />

        {/* Bottom interior ellipse (3D depth) */}
        <ellipse
          cx={beakerLeft + beakerWidth/2}
          cy={beakerBottom - 2}
          rx={beakerWidth/2 - 3}
          ry="5"
          fill={`url(#bottom-${id})`}
        />

        {/* Glass body fill */}
        <rect
          x={beakerLeft + 1}
          y={beakerTop + 1}
          width={beakerWidth - 2}
          height={beakerBottom - beakerTop - 1}
          fill={`url(#glass-${id})`}
        />

        {/* Top rim - thickened edge */}
        <ellipse
          cx={beakerLeft + beakerWidth/2}
          cy={beakerTop}
          rx={beakerWidth/2 + 2}
          ry="4"
          fill={`url(#rim-${id})`}
          stroke="#6A8AA5"
          strokeWidth="1"
        />

        {/* Pouring spout */}
        <path
          d={`M${beakerLeft - 2} ${beakerTop}
              Q${beakerLeft - 12} ${beakerTop - 2} ${beakerLeft - 10} ${beakerTop - 12}
              Q${beakerLeft - 8} ${beakerTop - 18} ${beakerLeft + 5} ${beakerTop - 10}
              Q${beakerLeft + 8} ${beakerTop - 5} ${beakerLeft + 5} ${beakerTop}`}
          fill={`url(#glass-${id})`}
          stroke="#7A9BB8"
          strokeWidth="2"
        />
      </g>

      {/* === LIQUID CONTENTS === */}
      {fillLevel > 0 && (
        <g clipPath={`url(#liquid-clip-${id})`}>
          {/* Main liquid body */}
          <rect
            x={beakerLeft + 3}
            y={fillTop}
            width={beakerWidth - 6}
            height={fillHeight + 5}
            fill={muddy && !cleared ? `url(#muddy-${id})` : `url(#liquid-${id})`}
          />

          {/* Liquid surface (meniscus) */}
          <ellipse
            cx={beakerLeft + beakerWidth/2}
            cy={fillTop}
            rx={beakerWidth/2 - 5}
            ry="3"
            fill={liquidColor}
            opacity="0.9"
          />

          {/* Surface highlight */}
          <ellipse
            cx={beakerLeft + beakerWidth/2 - 10}
            cy={fillTop}
            rx="15"
            ry="2"
            fill="rgba(255,255,255,0.4)"
          />

          {/* Floating particles for muddy water */}
          {muddy && !cleared && (
            <g opacity="0.7">
              {[...Array(10)].map((_, i) => (
                <circle
                  key={i}
                  cx={beakerLeft + 15 + (i % 4) * 20}
                  cy={fillTop + 10 + Math.floor(i / 4) * 25}
                  r={1.5 + (i % 3) * 0.5}
                  fill="#4A3A2A"
                >
                  <animate
                    attributeName="cy"
                    values={`${fillTop + 10 + Math.floor(i / 4) * 25};${fillTop + 15 + Math.floor(i / 4) * 25};${fillTop + 10 + Math.floor(i / 4) * 25}`}
                    dur={`${1.5 + (i % 3) * 0.5}s`}
                    repeatCount="indefinite"
                  />
                </circle>
              ))}
            </g>
          )}
        </g>
      )}

      {/* === PRECIPITATE/SEDIMENT === */}
      {precipitate > 0 && (
        <g clipPath={`url(#liquid-clip-${id})`}>
          <rect
            x={beakerLeft + 3}
            y={beakerBottom - 7 - precipHeight}
            width={beakerWidth - 6}
            height={precipHeight}
            fill={`url(#precip-${id})`}
          />
          {/* Sediment surface texture */}
          <ellipse
            cx={beakerLeft + beakerWidth/2}
            cy={beakerBottom - 7 - precipHeight}
            rx={beakerWidth/2 - 5}
            ry="2"
            fill="#C4B896"
            opacity="0.7"
          />
        </g>
      )}

      {/* === GLASS HIGHLIGHTS === */}
      {/* Left edge shine */}
      <rect
        x={beakerLeft + 3}
        y={beakerTop + 8}
        width="4"
        height={beakerBottom - beakerTop - 20}
        fill="rgba(255,255,255,0.5)"
        rx="2"
      />

      {/* Secondary shine */}
      <rect
        x={beakerLeft + 10}
        y={beakerTop + 15}
        width="2"
        height={beakerBottom - beakerTop - 35}
        fill="rgba(255,255,255,0.25)"
        rx="1"
      />

      {/* === GRADUATION MARKS === */}
      {[50, 100, 150, 200, 250, ...(size === 500 ? [300, 400, 500] : [])]
        .filter(mark => mark <= size)
        .map((mark) => {
          const markRatio = mark / size
          const y = beakerBottom - 8 - (markRatio * innerHeight)
          const isMain = mark % 100 === 0
          return (
            <g key={mark}>
              <line
                x1={beakerRight - (isMain ? 12 : 8)}
                y1={y}
                x2={beakerRight - 3}
                y2={y}
                stroke="#5A7A95"
                strokeWidth={isMain ? 1.5 : 1}
              />
              {isMain && (
                <text
                  x={beakerRight - 14}
                  y={y + 3}
                  fontSize="9"
                  fill="#5A7A95"
                  textAnchor="end"
                  fontFamily="Arial, sans-serif"
                >
                  {mark}
                </text>
              )}
            </g>
          )
        })}

      {/* mL label */}
      <text
        x={beakerRight - 8}
        y={beakerTop + 18}
        fontSize="8"
        fill="#5A7A95"
        textAnchor="end"
        fontFamily="Arial, sans-serif"
      >
        mL
      </text>

      {/* === LABEL === */}
      {label && (
        <text
          x={beakerLeft + beakerWidth/2}
          y="162"
          fontSize="12"
          fill="var(--text-primary)"
          textAnchor="middle"
          fontWeight="600"
        >
          {label}
        </text>
      )}
    </svg>
  )
}

export default Beaker
